import React from 'react'
import {
  Badge,
  Box,
  Flex,
  Heading,
  Image,
  ListItem,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  List,
  Select,
} from '../../../../utils/chakraMonomorphic'

import {useFaceOffLoad} from '../../../../hooks/useFaceOffRun'
import {api} from '../../../../services/api'
import {
  FaceOffRunDetail,
  FaceOffRunFace,
  FaceOffRunListEntry,
} from '../../../../services/coreApi/faceoff'
import copy from './copy.json'
import {fillCopy} from './constants'
import {AlbumLink} from './FaceOffFaceCard'
import {formatStartedAt} from './FaceOffRunList'
import {
  MergedFace,
  compareFaces,
  formatDelta,
  formatScore,
  faceState,
  mergeFaces,
  scanName,
} from './faceOffFaces'
import {
  BOX_COLORS,
  FaceOffLightbox,
  FaceOffPhoto,
  PhotoBox,
} from './FaceOffPhoto'

interface PhotoGroup {
  photoId: number
  faces: FaceOffRunFace[]
  accepted: number
  crossCamp: boolean
}

// A golden photo, so the picker lists the photos the run recorded no face on.
export interface InspectorPhoto {
  photoId: number
  url: string
}

const groupByPhoto = (
  faces: FaceOffRunFace[],
  photos: InspectorPhoto[] = [],
): PhotoGroup[] => {
  const groups = new Map<number, PhotoGroup>()
  photos.forEach((p) =>
    groups.set(p.photoId, {
      photoId: p.photoId,
      faces: [],
      accepted: 0,
      crossCamp: false,
    }),
  )
  faces.forEach((f) => {
    const g = groups.get(f.photoId) ?? {
      photoId: f.photoId,
      faces: [],
      accepted: 0,
      crossCamp: false,
    }
    g.faces.push(f)
    if (f.accepted) g.accepted += 1
    if (f.path === 'cross_camp') g.crossCamp = true
    groups.set(f.photoId, g)
  })
  return Array.from(groups.values()).sort((a, b) => a.photoId - b.photoId)
}

// Side A draws this run, side B the compared run, for the same merged boxes.
const boxColor = (
  face: MergedFace<FaceOffRunFace>,
  sideB: boolean,
): string | null => {
  const present = face.candidates.some((c) =>
    sideB ? c.previous !== null : c.score !== null,
  )
  if (!present) return null
  if (face.candidates.some((c) => c.change === 'flip')) return BOX_COLORS.flip
  const accepted = face.candidates.some((c) =>
    sideB ? c.previous?.accepted : c.accepted,
  )
  return accepted ? BOX_COLORS.accepted : BOX_COLORS.recorded
}

const ScoreCell: React.FC<{score: number | null}> = ({score}) => (
  <>{formatScore(score)}</>
)

/** The candidates-by-photo table: the margin each photo was tagged with. */
const CandidatesByPhoto: React.FC<{
  faces: FaceOffRunFace[]
  onPick: (photoId: number) => void
}> = ({faces, onPick}) => {
  const rows = groupByPhoto(faces)
    .map((g) => {
      const acc = g.faces
        .filter((f) => f.accepted && f.score !== null)
        .map((f) => f.score as number)
      const near = g.faces
        .filter((f) => !f.accepted && f.score !== null)
        .map((f) => f.score as number)
      return {
        photoId: g.photoId,
        acc: acc.length,
        near: near.length,
        weakest: acc.length ? Math.min(...acc) : null,
        closest: near.length ? Math.max(...near) : null,
      }
    })
    .sort((x, y) => (x.weakest ?? 999) - (y.weakest ?? 999))
  const accepted = faces.filter((f) => f.accepted).length
  const every = [...faces].sort(
    (x, y) => x.photoId - y.photoId || (y.score ?? 0) - (x.score ?? 0),
  )
  const cols = copy.inspector.candidateColumns
  return (
    <Box mt={6} data-testid='face-off-candidates'>
      <Heading size='sm' mb={2}>
        {copy.inspector.candidatesHeading}
      </Heading>
      <Text
        fontSize='sm'
        color='var(--fo-muted)'
        mb={1}
        data-testid='face-off-candidates-note'>
        {copy.inspector.candidatesNote}
      </Text>
      <Text fontSize='sm' color='var(--fo-muted)' mb={2}>
        {fillCopy(copy.inspector.candidatesHelp, {
          accepted,
          near: faces.length - accepted,
          photos: rows.length,
        })}
      </Text>
      <Table size='sm'>
        <Thead>
          <Tr>
            <Th>{cols.photo}</Th>
            <Th isNumeric>{cols.accepted}</Th>
            <Th isNumeric>{cols.near}</Th>
            <Th isNumeric>{cols.weakest}</Th>
            <Th isNumeric>{cols.closest}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((r) => (
            <Tr
              key={r.photoId}
              cursor='pointer'
              _hover={{bg: 'var(--fo-hover)'}}
              onClick={(): void => onPick(r.photoId)}
              data-testid='face-off-candidate-photo'>
              <Td>{r.photoId}</Td>
              <Td isNumeric>{r.acc}</Td>
              <Td isNumeric>{r.near || ''}</Td>
              <Td isNumeric>
                <ScoreCell score={r.weakest} />
              </Td>
              <Td isNumeric>{r.near ? formatScore(r.closest) : ''}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      <Box as='details' mt={3}>
        <Box as='summary' cursor='pointer' fontSize='sm'>
          {fillCopy(copy.inspector.everyCandidate, {count: faces.length})}
        </Box>
        <Table size='sm'>
          <Thead>
            <Tr>
              <Th>{cols.photo}</Th>
              <Th>{cols.person}</Th>
              <Th isNumeric>{cols.rank}</Th>
              <Th isNumeric>{cols.score}</Th>
              <Th isNumeric>{cols.cutoff}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {every.map((f) => (
              <Tr
                key={`${f.photoId}:${f.personId}:${f.path}:${f.rank}`}
                color={f.accepted ? undefined : 'var(--fo-muted)'}>
                <Td>{f.photoId}</Td>
                <Td>{f.personId}</Td>
                <Td isNumeric>{f.rank}</Td>
                <Td isNumeric>{formatScore(f.score)}</Td>
                <Td isNumeric>{f.threshold}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  )
}

/** A golden case's truth, keyed by `${photoId}:${personId}`. */
export type TruthMap = Record<string, string>

const NO_PHOTOS: InspectorPhoto[] = []
const NO_RUNS: FaceOffRunListEntry[] = []

/**
 * The run inspector: every recorded face of a run, photo by photo, drawn on
 * this run's photo beside a compared run, with the camper's frozen training
 * photos per match. The compared run starts as the run before, unless this
 * run is a re-baseline; any earlier run can be picked instead.
 */
export const FaceOffInspector: React.FC<{
  detail: FaceOffRunDetail
  clientId: number
  truth?: TruthMap
  // The run's golden photos, so photos with no recorded face are listed too.
  photos?: InspectorPhoto[]
  // Every run, newest first, for the compare picker.
  runs?: FaceOffRunListEntry[]
}> = ({detail, clientId, truth = {}, photos = NO_PHOTOS, runs = NO_RUNS}) => {
  const groups = React.useMemo(
    () => groupByPhoto(detail.faces, photos),
    [detail, photos],
  )
  const defaultCompare =
    detail.previous !== null && !detail.rebaseline ? detail.previous : ''
  const [compareId, setCompareId] = React.useState(defaultCompare)
  React.useEffect(() => setCompareId(defaultCompare), [defaultCompare])
  // The run before is already in `detail`; any other run is fetched.
  const fetchId = compareId === defaultCompare ? '' : compareId
  const other = useFaceOffLoad(fetchId, () =>
    fetchId
      ? api.faceoff.getFaceOffRun(fetchId)
      : Promise.resolve({result: 'success' as const, data: null}),
  )
  const faces = React.useMemo((): FaceOffRunFace[] | null => {
    // Compared with nothing, no face changed.
    if (!compareId)
      return detail.faces.map((f) => ({...f, previous: null, change: ''}))
    if (!fetchId) return detail.faces
    if (!other.data) return null
    return compareFaces(detail.faces, other.data.faces, detail.run.band)
  }, [detail, compareId, fetchId, other.data])
  const earlier = runs.filter(
    (r) => Date.parse(r.startedAt) < Date.parse(detail.run.startedAt),
  )
  const photoUrls = React.useMemo(() => {
    const out: Record<string, string> = {}
    photos.forEach((p) => {
      out[String(p.photoId)] = p.url
    })
    return {...out, ...detail.photoUrls}
  }, [detail, photos])
  const [photoId, setPhotoId] = React.useState<number | null>(null)
  const [selected, setSelected] = React.useState<number | null>(null)
  // The enlarged view: this run's photo, and the training photo beside it.
  const [enlarged, setEnlarged] = React.useState<{side: string | null} | null>(
    null,
  )
  const photoRef = React.useRef<HTMLDivElement | null>(null)

  // A late load (the golden photos) keeps the picked photo if it is still listed.
  React.useEffect(() => {
    if (photoId !== null && groups.some((g) => g.photoId === photoId)) return
    setPhotoId(groups.length ? groups[0].photoId : null)
    setSelected(null)
  }, [groups, photoId])

  if (groups.length === 0) {
    return (
      <Box mt={8} data-testid='face-off-inspector'>
        <Heading size='sm' mb={2}>
          {copy.inspector.heading}
        </Heading>
        <Text fontSize='sm' data-testid='face-off-inspector-empty'>
          {copy.inspector.noFaces}
        </Text>
      </Box>
    )
  }

  const shown = groups.find((g) => g.photoId === photoId) ?? groups[0]
  // The shown photo's faces, each paired with the compared run's.
  const group = {
    ...shown,
    faces: faces
      ? faces.filter((f) => f.photoId === shown.photoId)
      : shown.faces,
  }
  const merged = mergeFaces(group.faces)
  const src = photoUrls[String(group.photoId)]
  const compare = compareId !== '' && faces !== null

  const pick = (id: number): void => {
    setPhotoId(id)
    setSelected(null)
    photoRef.current?.scrollIntoView?.({block: 'center', behavior: 'smooth'})
  }
  const select = (i: number): void =>
    setSelected((cur) => (cur === i ? null : i))

  const boxesFor = (sideB: boolean): PhotoBox[] =>
    merged.flatMap((m, i) => {
      const color = boxColor(m, sideB)
      if (!m.bbox || !color) return []
      return [
        {
          bbox: m.bbox,
          color,
          label: fillCopy(copy.inspector.face, {n: i + 1}),
          selected: selected === i,
          dim: selected !== null && selected !== i,
          onClick: (): void => select(i),
        },
      ]
    })

  let beforeCaption = copy.inspector.noCompare
  if (compare)
    beforeCaption = fillCopy(copy.inspector.runBefore, {runId: compareId})
  else if (compareId && other.status === 'rejected')
    beforeCaption = fillCopy(copy.inspector.compareError, {runId: compareId})
  else if (compareId)
    beforeCaption = fillCopy(copy.inspector.compareLoading, {runId: compareId})
  else if (detail.rebaseline) beforeCaption = copy.inspector.rebaseline

  // Enlarging the photo shows the selected face's first training photo beside it.
  const selectedPerson =
    selected !== null ? merged[selected]?.candidates[0]?.personId : undefined
  const selectedRef =
    selectedPerson !== undefined
      ? detail.refUrls[String(selectedPerson)]?.[0] ?? null
      : null

  const cols = copy.inspector.columns
  const rows = merged.flatMap((m, i) => m.candidates.map((c) => ({i, c})))

  return (
    <Box mt={8} data-testid='face-off-inspector'>
      <Heading size='sm' mb={1}>
        {copy.inspector.heading}
      </Heading>
      <Text fontSize='sm' color='var(--fo-muted)' mb={2}>
        {copy.inspector.help}
      </Text>
      <List fontSize='xs' color='var(--fo-muted)' mb={3} spacing={1}>
        {copy.inspector.legend.map((line) => (
          <ListItem key={line} data-testid='face-off-inspector-legend'>
            {line}
          </ListItem>
        ))}
      </List>
      <Flex
        as='label'
        alignItems='center'
        gap={2}
        fontSize='sm'
        mb={3}
        maxW='480px'>
        {copy.inspector.compareWith}
        <Select
          size='sm'
          value={compareId}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>): void =>
            setCompareId(e.target.value)
          }
          data-testid='face-off-inspector-compare'>
          <option value=''>{copy.inspector.compareNone}</option>
          {/* The run before stays listed when the run list is missing. */}
          {earlier.length === 0 && detail.previous && (
            <option value={detail.previous}>{detail.previous}</option>
          )}
          {earlier.map((r) => (
            <option key={r.runId} value={r.runId}>
              {fillCopy(copy.inspector.compareOption, {
                startedAt: formatStartedAt(r.startedAt),
                goldenVersion: r.goldenVersion,
              })}
            </option>
          ))}
        </Select>
      </Flex>
      <Flex direction='column' gap={3}>
        <Flex gap={1} overflowX='auto' pb={1}>
          {groups.map((g) => (
            <Box
              key={g.photoId}
              flex='none'
              px={2}
              py={1}
              borderRadius='md'
              borderWidth='1px'
              borderColor={
                g.photoId === group.photoId ? 'var(--fo-accent)' : 'transparent'
              }
              _hover={{bg: 'var(--fo-hover)'}}
              cursor='pointer'
              bg={
                g.photoId === group.photoId ? 'var(--fo-selected)' : undefined
              }
              onClick={(): void => pick(g.photoId)}
              data-testid='face-off-inspector-photo'>
              <Text fontSize='sm'>
                {fillCopy(copy.inspector.photoItem, {photoId: g.photoId})}
              </Text>
              <Text fontSize='xs' color='var(--fo-muted)'>
                {g.faces.length === 0
                  ? copy.inspector.noFaceRecorded
                  : fillCopy(copy.inspector.photoCounts, {
                      accepted: g.accepted,
                      recorded: g.faces.length - g.accepted,
                    })}
                {g.crossCamp ? copy.inspector.crossCamp : ''}
              </Text>
            </Box>
          ))}
        </Flex>
        <Box flex='1' minW={0}>
          {group.faces.length > 0 && (
            <Box mb={2}>
              <AlbumLink
                face={group.faces[0]}
                clientId={clientId}
                testId='face-off-inspector'
              />
            </Box>
          )}
          <Flex gap={4} mb={4} ref={photoRef} flexWrap='wrap'>
            <Box flex='1' minW='280px'>
              <Text fontSize='xs' color='var(--fo-muted)' mb={1}>
                {fillCopy(copy.inspector.thisRun, {runId: detail.run.runId})}
              </Text>
              <FaceOffPhoto
                src={src}
                alt={fillCopy(copy.inspector.photoItem, {
                  photoId: group.photoId,
                })}
                boxes={boxesFor(false)}
                maxH='75vh'
                onClick={(): void => setEnlarged({side: selectedRef})}
                testId='face-off-inspector-after'
              />
            </Box>
            <Box flex='1' minW='280px'>
              <Text fontSize='xs' color='var(--fo-muted)' mb={1}>
                {beforeCaption}
              </Text>
              {compare && (
                <FaceOffPhoto
                  src={src}
                  alt={fillCopy(copy.inspector.photoItem, {
                    photoId: group.photoId,
                  })}
                  boxes={boxesFor(true)}
                  maxH='75vh'
                  testId='face-off-inspector-before'
                />
              )}
            </Box>
          </Flex>
          {group.faces.length === 0 && (
            <Text
              fontSize='sm'
              color='var(--fo-muted)'
              mb={2}
              data-testid='face-off-inspector-no-faces-on-photo'>
              {copy.inspector.noFacesOnPhoto}
            </Text>
          )}
          <Table size='sm'>
            <Thead>
              <Tr>
                <Th>{cols.face}</Th>
                <Th>{cols.scan}</Th>
                <Th>{cols.camper}</Th>
                <Th isNumeric>{cols.score}</Th>
                <Th>{cols.state}</Th>
                {compare && <Th>{cols.before}</Th>}
                {compare && <Th>{cols.change}</Th>}
                <Th>{cols.truth}</Th>
                <Th>{cols.references}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map(({i, c}) => {
                const refs = detail.refUrls[String(c.personId)] ?? []
                const delta =
                  c.previous?.score != null && c.score !== null
                    ? formatDelta(c.score - c.previous.score)
                    : ''
                return (
                  <Tr
                    key={`${i}:${c.path}:${c.personId}:${c.rank}`}
                    bg={selected === i ? 'var(--fo-selected)' : undefined}
                    cursor='pointer'
                    onClick={(): void => select(i)}
                    data-testid='face-off-inspector-row'>
                    <Td>{i + 1}</Td>
                    <Td>
                      {scanName(c.path)}{' '}
                      {c.path === 'retroactive' && (
                        <Badge>{copy.inspector.retroactive}</Badge>
                      )}
                    </Td>
                    <Td>{c.personId}</Td>
                    <Td isNumeric>{formatScore(c.score)}</Td>
                    <Td
                      color={c.accepted ? 'var(--fo-green)' : 'var(--fo-amber)'}
                      whiteSpace='nowrap'>
                      {fillCopy(copy.inspector.stateLine, {
                        state: faceState(c),
                        threshold: c.threshold,
                      })}
                    </Td>
                    {compare && (
                      <Td whiteSpace='nowrap'>
                        {c.previous
                          ? `${formatScore(c.previous.score)} ${faceState(
                              c.previous,
                            )}`
                          : faceState(null)}
                      </Td>
                    )}
                    {compare && (
                      <Td
                        color={
                          c.change === 'flip' ? 'var(--fo-red)' : undefined
                        }
                        fontWeight={c.change === 'flip' ? 'bold' : undefined}
                        data-testid='face-off-inspector-change'>
                        {copy.inspector.changes[c.change]}
                        {delta && ` (${delta})`}
                      </Td>
                    )}
                    <Td data-testid='face-off-inspector-truth'>
                      {truth[`${c.photoId}:${c.personId}`] ??
                        copy.inspector.noTruth}
                    </Td>
                    <Td>
                      <Flex gap={1}>
                        {refs.length === 0 ? (
                          <Text fontSize='xs' color='var(--fo-muted)'>
                            {copy.inspector.noRefs}
                          </Text>
                        ) : (
                          refs.map((ref) => (
                            <Image
                              key={ref}
                              src={ref}
                              alt={copy.diff.training}
                              h='48px'
                              borderRadius='sm'
                              cursor='zoom-in'
                              onClick={(e: React.MouseEvent): void => {
                                e.stopPropagation()
                                setEnlarged({side: ref})
                              }}
                              data-testid='face-off-inspector-ref'
                            />
                          ))
                        )}
                      </Flex>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </Box>
      </Flex>
      <CandidatesByPhoto faces={detail.faces} onPick={pick} />
      {enlarged && (
        <FaceOffLightbox
          src={src ?? ''}
          alt={fillCopy(copy.inspector.photoItem, {photoId: group.photoId})}
          boxes={boxesFor(false)}
          side={
            enlarged.side ? {src: enlarged.side, alt: copy.diff.training} : null
          }
          onClose={(): void => setEnlarged(null)}
        />
      )}
    </Box>
  )
}
